import { createAction } from 'redux-actions';
import localStore from 'store';
import * as actionType from './actionTypes';
import client from '../../apollo';
import { ruleToApiParameters } from '../../utilities/eligibilityEngine';
import eligibilityProductsQuery from '../../graphql/eligibilityProductsQuery';
import eligibilityRulesQuery from '../../graphql/eligibilityRulesQuery';
import updateRulesMutation from '../../graphql/updateRulesMutation';
import upsertRuleNoteMutation from '../../graphql/upsertRuleNoteMutation';
import externalHistoryQuery from '../../graphql/externalHistoryQuery';
import externalHistoryByRuleKeyQuery from '../../graphql/externalHistoryByRuleKeyQuery';

export const setProducts = createAction(actionType.SET_PRODUCTS);
export const setProductsLoading = createAction(actionType.SET_PRODUCTS_LOADING);
export const setProductsError = createAction(actionType.SET_PRODUCTS_ERROR);
export const setSelectedProduct = createAction(actionType.SET_SELECTED_PRODUCT);
export const setRules = createAction(actionType.SET_RULES);
export const setRulesLoading = createAction(actionType.SET_RULES_LOADING);
export const setRulesError = createAction(actionType.SET_RULES_ERROR);
export const setNextEffectiveFrom = createAction(
  actionType.SET_NEXT_EFFECTIVE_FROM
);
export const setHasUnsavedChanges = createAction(
  actionType.SET_HAS_UNSAVED_CHANGES
);
export const updateRule = createAction(actionType.UPDATE_RULE);
export const setPendingActivation = createAction(
  actionType.SET_PENDING_ACTIVATION
);
export const addRule = createAction(actionType.ADD_RULE);
export const addRuleSorted = createAction(actionType.ADD_RULE_SORTED);
export const removeRule = createAction(actionType.REMOVE_RULE);

// Monotonically increasing counter used to discard stale fetchRules responses.
let rulesRequestCounter = 0;

export const fetchProducts = () => async dispatch => {
  dispatch(setProductsLoading(true));
  try {
    const result = await client.query({
      query: eligibilityProductsQuery,
      fetchPolicy: 'network-only',
    });
    // Rename "Seller Assurance" to "Seller Guarantee" for display only;
    // the underlying product data from the backend is left unchanged.
    const productsData = (result.data.externalProducts || []).map(product => ({
      ...product,
      displayName:
        product.displayName === 'Seller Assurance'
          ? 'Seller Guarantee'
          : product.displayName,
    }));
    dispatch(setProducts(productsData));
    // Determine the product to select: prefer the one saved in localStorage
    // (if it still exists in the list), otherwise fall back to the first product.
    const savedProduct = localStore.get('eligibilitySelectedProduct');
    const productToSelect = savedProduct && productsData.some(p => p.categoryCode === savedProduct)
      ? savedProduct
      : productsData[0]?.categoryCode || '';
    dispatch(setSelectedProduct(productToSelect));
  } catch (err) {
    // Reducer will stop loading when SET_PRODUCTS_ERROR is dispatched.
    dispatch(setProductsError(err));
  }
};

const toLocalISOString = utcString => {
  // Force parsing as UTC: append 'Z' if no timezone designator is present
  const normalized = /[-Z+]\d*$/.test(utcString) ? utcString : `${utcString}Z`;
  const date = new Date(normalized);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().replace('Z', '');
};

export const fetchRules = productCode => async dispatch => {
  if (!productCode) return;
  rulesRequestCounter += 1;
  const requestId = rulesRequestCounter;
  dispatch(setRulesLoading(true));
  dispatch(setRulesError(null));
  dispatch(setNextEffectiveFrom(null));
  try {
    const nextResult = await client.query({
      query: eligibilityRulesQuery,
      variables: { productCode: String(productCode), configType: 'NEXT' },
      fetchPolicy: 'network-only',
    });
    const nextConfig = nextResult.data.externalRulesByProductCode;
    if (nextConfig && nextConfig.effectiveFrom != null) {
      dispatch(setRules({ rules: nextConfig.rules || [], productCode }));
      dispatch(
        setNextEffectiveFrom(toLocalISOString(nextConfig.effectiveFrom))
      );
    } else {
      const activeResult = await client.query({
        query: eligibilityRulesQuery,
        variables: { productCode: String(productCode), configType: 'ACTIVE' },
        fetchPolicy: 'network-only',
      });
      const activeConfig = activeResult.data.externalRulesByProductCode;
      dispatch(
        setRules({
          rules: (activeConfig && activeConfig.rules) || [],
          productCode,
        })
      );
      dispatch(setNextEffectiveFrom(null));
    }
  } catch (err) {
    if (requestId === rulesRequestCounter) {
      dispatch(setRulesError(err));
    }
  } finally {
    if (requestId === rulesRequestCounter) {
      dispatch(setRulesLoading(false));
    }
  }
};

export const publishRules = ({ effectiveFrom }) => async(dispatch, getState) => {
  const state = getState();
  const { selectedProduct, rules } = state.data.eligibilityEngine;

  const ruleConfiguration = rules.map(rule => ({
    id: rule.id,
    category: rule.category,
    priority: rule.priority,
    parameters: ruleToApiParameters(rule),
    actionOnFail: rule.actionOnFail,
    actionOnNull: rule.actionOnNull,
  }));

  try {
    const result = await client.mutate({
      mutation: updateRulesMutation,
      variables: {
        input: {
          updateRules: {
            productCode: selectedProduct,
            effectiveFrom,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ruleConfiguration,
          },
        },
      },
    });

    if (result.data.updateRules.success) {
      dispatch(setHasUnsavedChanges(false));
      const targetDate = new Date(effectiveFrom);
      if (targetDate > new Date()) {
        dispatch(setNextEffectiveFrom(effectiveFrom));
      }
      return { success: true };
    }
    return { success: false };
  } catch (err) {
    const errorMessage = err.graphQLErrors?.[0]?.message
        || 'Failed to publish. Please try again.';
    return { success: false, error: err, errorMessage };
  }
};

export const upsertRuleNote = ({ productCode, ruleId, note }) => async dispatch => {
  try {
    await client.mutate({
      mutation: upsertRuleNoteMutation,
      variables: {
        input: { ruleNote: { productCode, ruleId, note } },
      },
    });
  } catch (err) {
    dispatch(setRulesError(err));
  }
};

export const fetchExternalHistory = ({ productCode, from, to }) => async() => {
  const { data } = await client.query({
    query: externalHistoryQuery,
    variables: { productCode, ...(from && { from }), ...(to && { to }) },
    fetchPolicy: 'network-only',
  });
  return data.externalHistory;
};

export const fetchExternalHistoryByRuleKey = ({
  productCode, ruleKey, from, to,
}) => async() => {
  const { data } = await client.query({
    query: externalHistoryByRuleKeyQuery,
    variables: {
      productCode,
      ruleKey,
      ...(from && { from }),
      ...(to && { to }),
    },
    fetchPolicy: 'network-only',
  });
  return data.externalHistoryByRuleKey;
};
